import React, { useCallback, useEffect, useState } from 'react';

import { generate } from '@pdfme/generator';
import {
  DialogModal,
  Menu,
  MenuItem,
  ModalBox,
  PopupState,
  Table,
  bindMenu,
  bindTrigger,
} from 'components';
import { useIsMobile } from 'hooks';
import {
  ArrowRight2,
  Bank,
  Brush,
  DocumentDownload,
  Edit2,
  Printer,
  Trash,
} from 'iconsax-react';

import { getSampleCheckTemplate } from '../CheckDesigner/sampleData';
import { BankCreateEditForm } from './BankCreateEditForm';
import { BlankCheckPrintModal } from './BlankCheckPrintModal';
import { SimpleBankDesigner } from './SimpleBankDesigner';
import { bankColumns } from './bankColumns';

import {
  Template,
  getInputFromTemplate,
  useSelector,
  useTranslation,
} from 'third-party';

import {
  DEFAULT_TIMEZONE,
  PersisTableFilters,
  PersisTableSort,
  PersisTables,
} from 'constants/common';

import { useBank } from 'modules/bank';
import { useSettings } from 'modules/setting';

import {
  appSelectors,
  bankSelectors,
  billingSelectors,
  settingSelectors,
} from 'selectors';

import { FilterValue } from 'components/Table/types';

import { plugins } from 'features/CheckDesigner/CheckDesigner';

import {
  extractFiltersFromSavedColumns,
  mergeSavedColumnsWithExisting,
} from 'utils/app';
import { removeNullFields } from 'utils/helper';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import {
  APIBank,
  APISettingName,
  APISortOrder,
  APIUserRight,
  APIUserType,
  PrintType,
} from 'types/api';

import { palette } from 'styles/theme';

const emptyData = {
  id: '',
  organizationId: '',
  payeeId: '',
  bankId: '',
  owner: '',
  bankInfo: '',
  memo: '',
  amountInText: '',
  nameOnCheck: '',
  amount: '',
  checkDate: '',
  checkNumber: '',
  verifyCode: '',
  verifyUri: '',
  referenceId: '',
  bankName: '',
  companyName: '',
  companyAddress: '',
  faqLink: '',
  companyAddressMiddle: '',
  nameOnCheckMiddle: '',
  checkDateLower: '',
  nameOnCheckLower: '',
  checkNumberLower: '',
  payTo: '',
  payFrom: '',
  memoBottom: '',
};

const DEFAULT_BANK_SORT = {
  sortField: 'createdOn',
  sortOrder: APISortOrder.DESC,
};

export const BanksList = () => {
  const { t } = useTranslation();
  const organizationUserType = useSelector(appSelectors.organizationUserType);
  const organizationUserRights = useSelector(
    appSelectors.organizationUserRights,
  );

  const canReveal =
    organizationUserType === APIUserType.Admin ||
    (organizationUserType === APIUserType.Editor &&
      !!organizationUserRights?.includes(APIUserRight.BankAccess));

  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );

  const [columns, setColumns] = useState(() =>
    bankColumns(canReveal, timezoneValue),
  );
  const [selectedItem, setSelectedItem] = useState<APIBank | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isBlankCheckModalOpen, setIsBlankCheckModalOpen] = useState(false);

  const {
    loadBanks,
    loadMoreBanks,
    updateBankFilters,
    deleteBank,
    clearBankList,
  } = useBank();

  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const organizationId = useSelector(appSelectors.organizationId);
  const items = useSelector(bankSelectors.bankList);
  const banksListLoaded = useSelector(bankSelectors.banksListLoaded);
  const allBanksLoaded = useSelector(bankSelectors.allBanksLoaded);
  const bankFilters = useSelector(bankSelectors.bankFilters);
  const loadBanksPending = useSelector(bankSelectors.loadBanksPending);
  const loadMoreBanksPending = useSelector(bankSelectors.loadMoreBanksPending);
  const isMobile = useIsMobile();
  const isOnPaidPlan = useSelector(billingSelectors.isOnPaidPlan);

  const { loadCheckTemplate } = useSettings();

  const downloadPdf = useCallback(
    async (bankId: string, type: PrintType) => {
      const defaultTemplate = getSampleCheckTemplate();
      const result = await loadCheckTemplate(bankId, type);
      let template: Template = defaultTemplate;
      if (result.payload) {
        template = removeNullFields(result.payload);
      }

      const inputs = getInputFromTemplate(template).map(input => ({
        ...input,
        ...emptyData,
      }));

      const pdf = await generate({ template, inputs, plugins });
      const blob = new Blob([pdf as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const it = document.createElement('a');
      it.href = url;
      it.download = `${type}.pdf`;
      it.click();
      URL.revokeObjectURL(url);
    },
    [loadCheckTemplate],
  );

  useEffect(() => {
    if (organizationId && loadedUserSettings) {
      loadBanks();
    }
  }, [organizationId, bankFilters, loadedUserSettings, loadBanks]);

  useEffect(() => {
    const savedColumns = getFromStorage(
      PersisTables.Banks,
      bankColumns(canReveal, timezoneValue),
    );
    const savedSort = getFromStorage(PersisTableSort.Banks, DEFAULT_BANK_SORT);

    const columnFilters = savedColumns
      ? extractFiltersFromSavedColumns(savedColumns)
      : {};

    updateBankFilters({ ...columnFilters, searchSortOrders: [savedSort] });

    if (savedColumns) {
      setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    }
    setIsFilteringOn(getFromStorage(PersisTableFilters.Banks, false));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    return () => {
      clearBankList();
    };
  }, [clearBankList]);

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filter: value };
          }
          return column;
        }),
      );

      updateBankFilters({ ...bankFilters, [columnId]: value });
    },
    [bankFilters, columns, updateBankFilters],
  );

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filterValue: value };
          }
          return column;
        }),
      );

      updateBankFilters({ ...bankFilters, [columnId]: value });
    },
    [bankFilters, columns, updateBankFilters],
  );

  const onFilterReset = useCallback(() => {
    updateBankFilters({});
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [updateBankFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column => {
          if (column.dataId === columnId) {
            return { ...column, hidden: !value };
          }
          return column;
        }),
      );
    },
    [],
  );

  const rowOptionsMenu = useCallback(
    (item: APIBank, onRowMenuClose: () => void): React.ReactElement[] => [
      <MenuItem
        key={'edit'}
        onClick={() => {
          setIsModalOpen(true);
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <Edit2
          variant="TwoTone"
          color={palette.primary900}
          size="24"
          style={{ paddingRight: '0.5rem' }}
        />
        {t('table.edit')}
      </MenuItem>,
      <MenuItem
        key={'delete'}
        onClick={() => {
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <Trash
          variant="TwoTone"
          color={palette.primary900}
          size="24"
          style={{ paddingRight: '0.5rem' }}
        />
        {t('table.delete')}
      </MenuItem>,
      <PopupState
        key={'download-blank'}
        variant="popover"
        popupId={`download-blank-submenu-${item.id}`}
      >
        {popupState => (
          <>
            <MenuItem {...bindTrigger(popupState)}>
              <DocumentDownload
                variant="TwoTone"
                color={palette.primary900}
                size="24"
                style={{ paddingRight: '0.5rem' }}
              />
              {t('emptyECheckBlank.download')}
              <ArrowRight2
                variant="TwoTone"
                size="24"
                style={{ marginLeft: 'auto' }}
              />
            </MenuItem>
            <Menu
              {...bindMenu(popupState)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => {
                  downloadPdf(item.id, PrintType.WhitePaper);
                  popupState.close();
                  onRowMenuClose();
                }}
              >
                {t('emptyECheckBlank.whitePaper')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  downloadPdf(item.id, PrintType.Check);
                  popupState.close();
                  onRowMenuClose();
                }}
              >
                {t('emptyECheckBlank.checkPaper')}
              </MenuItem>
            </Menu>
          </>
        )}
      </PopupState>,

      ...(!isMobile
        ? [
            <MenuItem
              key={'checkDesigner'}
              onClick={() => {
                setSelectedItem(item);
                setIsBankModalOpen(true);
                onRowMenuClose();
              }}
            >
              <Brush
                variant="TwoTone"
                color={palette.primary900}
                size="24"
                style={{ paddingRight: '0.5rem' }}
              />
              {t('bankList.actions.checkDesigner')}
            </MenuItem>,
            ...(canReveal && isOnPaidPlan
              ? [
                  <MenuItem
                    key={'blankCheck'}
                    onClick={() => {
                      setSelectedItem(item);
                      setIsBlankCheckModalOpen(true);
                      onRowMenuClose();
                    }}
                  >
                    <Printer
                      variant="TwoTone"
                      color={palette.primary900}
                      size="24"
                      style={{ paddingRight: '0.5rem' }}
                    />
                    {t('bankList.actions.printBlankChecks')}
                  </MenuItem>,
                ]
              : []),
          ]
        : []),
    ],
    [t, isMobile, canReveal, isOnPaidPlan, downloadPdf],
  );

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
    setIsBankModalOpen(false);
    setIsBlankCheckModalOpen(false);
    setSelectedItem(null);
  }, []);

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string) => {
      const existingSortOrder = bankFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );

      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;

      const newSort = { sortField: columnDataId, sortOrder: newSortOrder };

      saveInStorage(PersisTableSort.Banks, newSort);

      updateBankFilters({
        ...bankFilters,
        searchSortOrders: [newSort],
      });
    },
    [bankFilters, updateBankFilters],
  );

  return (
    <>
      <Table<APIBank>
        draggable
        header={t('bankList.header')}
        subHeader={t('bankList.subHeader')}
        headerIcon={
          <Bank variant="TwoTone" color={palette.primary900} size="28" />
        }
        onSaveTableSettings={settings => {
          saveInStorage(PersisTables.Banks, settings);
        }}
        onResetTableSettings={() => {
          setColumns(bankColumns(canReveal, timezoneValue));
          updateBankFilters({ searchSortOrders: [DEFAULT_BANK_SORT] });
          removeFromStorage(PersisTables.Banks);
          removeFromStorage(PersisTableSort.Banks);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={setColumns}
        filterable
        filteringOn={isFilteringOn}
        onFilteringToggle={value => {
          setIsFilteringOn(value);
          saveInStorage(PersisTableFilters.Banks, value);
        }}
        allItemsLoaded={allBanksLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={columns}
        items={items || []}
        sortField={
          bankFilters.searchSortOrders?.[0]?.sortField as keyof APIBank
        }
        sortOrder={bankFilters.searchSortOrders?.[0]?.sortOrder}
        rowOptionsMenu={rowOptionsMenu}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        loadItemsPending={loadBanksPending}
        loadMoreItemsPending={loadMoreBanksPending}
        onLoadMoreClicked={loadMoreBanks}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={banksListLoaded}
        onNewItemButtonClicked={() => setIsModalOpen(true)}
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
      />
      {isModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '30rem'}
          height="38rem"
          onClose={onModalClose}
        >
          <BankCreateEditForm
            onModalClose={onModalClose}
            bankId={selectedItem?.id}
          />
        </ModalBox>
      )}
      {isBankModalOpen && (
        <ModalBox width="48rem" height="39rem" onClose={onModalClose}>
          <SimpleBankDesigner
            bankId={selectedItem?.id}
            onModalClose={onModalClose}
          />
        </ModalBox>
      )}
      {isBlankCheckModalOpen && selectedItem && (
        <ModalBox width="38rem" height="44rem" onClose={onModalClose}>
          <BlankCheckPrintModal
            bankId={selectedItem.id}
            onClose={onModalClose}
          />
        </ModalBox>
      )}
      <DialogModal
        onClose={() => setSelectedItem(null)}
        open={
          !!selectedItem &&
          !isModalOpen &&
          !isBankModalOpen &&
          !isBlankCheckModalOpen
        }
        description={t('bankList.deleteConfirm')}
        handleActionClick={() => {
          if (selectedItem) {
            deleteBank(selectedItem.id);
          }
          setSelectedItem(null);
        }}
      />
    </>
  );
};
